import { Injectable } from '@angular/core';
import * as THREE from '@phdsoft/three';
import { PointShapeGeometryCache, PointShapeType } from './point-shape-geometry-cache';
import { OUTLINE_LAYER_PRIMARY, OUTLINE_LAYER_SECONDARY } from './selection-outline-pass';
import { Section } from 'src/app/platform/busines-data/Framework/Section';
import { GaugingElement } from 'src/app/platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';

/** Lookup record returned by resolveInstance — exported so Phase 3 can reference it. */
export interface InstanceLookup {
  mesh: THREE.InstancedMesh;
  instanceIndex: number;
}

/**
 * Manages the lifecycle of InstancedMesh batches for inspection point rendering.
 *
 * One InstancedMesh is created per (section, shapeType) pair. Each mesh uses a
 * shared BufferGeometry from PointShapeGeometryCache and a private material
 * from the internal material pool (one MeshBasicMaterial per shapeType).
 *
 * Phase 1 scope:
 *   - buildBatch: create and configure InstancedMesh
 *   - getRaycastTargets: return all managed InstancedMesh objects
 *
 * Phase 2 scope:
 *   - addInstance: growth data path with buffer growth
 *   - setInstanceVisible: zero-scale visibility toggle with saved-matrix restore
 *   - setInstanceColor: maps material.name to per-instance color via _COLOR_MAP
 *   - flushDirtyBuffers: deferred GPU buffer upload (dirty flag pattern)
 *   - getInstanceVisible: per-instance visibility query
 *   - getInstanceInfo / getInstanceWorldMatrix / getInstanceColor: read an element's slot (batch mesh + index), world matrix and colour
 *   - getMeshesForSection: access per-section mesh map
 *   - queueForSection / drainPendingData: pending-data buffer for unloaded sections
 *
 * Phase 3 scope:
 *   - syncInstance: sync InstancedMesh slot from phantom transform (drag path)
 *   - removeInstance: swap-and-pop removal for cancelled insertions
 *   - setInstanceColorDirect: write THREE.Color directly to instanceColor buffer
 *
 * Phase 4 scope:
 *   - acquireProxy: get or create a pooled THREE.Mesh proxy for OutlinePass selection
 *   - releaseProxy: return proxy to pool on deselect (stays in scene, invisible)
 *   - syncProxyMatrix: update proxy matrixWorld each drag frame
 *   - releaseAllProxies: bulk deselect — release all active proxies at once
 *   - destroy: full teardown — dispose proxies, InstancedMesh objects, all maps
 */
@Injectable({ providedIn: 'root' })
export class InspectionPointInstanceManager {
  /**
   * Primary storage: section.Guid -> (shapeType -> InstancedMesh).
   */
  private _meshes: Map<string, Map<PointShapeType, THREE.InstancedMesh>> = new Map();

  /**
   * Reverse index for raycast resolution.
   * Maps each InstancedMesh to a slot array where element references live.
   */
  private _instanceMap: Map<THREE.InstancedMesh, (GaugingElement | null)[]> = new Map();

  /**
   * Forward index: GaugingElement -> {mesh, instanceIndex}.
   */
  private _elementMap: Map<GaugingElement, InstanceLookup> = new Map();

  /**
   * Private material pool — one MeshBasicMaterial per PointShapeType.
   * Materials are created in _initMaterials() and disposed in destroy().
   */
  private _materials: Map<PointShapeType, THREE.MeshBasicMaterial> = new Map();

  // ---------------------------------------------------------------------------
  // Phase 2 fields
  // ---------------------------------------------------------------------------

  /** Meshes whose instanceMatrix.needsUpdate must be set at next flush. */
  private _matrixDirty: Set<THREE.InstancedMesh> = new Set();

  /** Meshes whose instanceColor.needsUpdate must be set at next flush. */
  private _colorDirty: Set<THREE.InstancedMesh> = new Set();

  /** Saved world matrices for hidden instances — used to restore on show. */
  private _savedMatrix: Map<GaugingElement, THREE.Matrix4> = new Map();

  /** Pre-computed zero-scale matrix — applied to hide individual instances (VIS-02). */
  private readonly _ZERO_MATRIX: THREE.Matrix4 = new THREE.Matrix4().makeScale(0, 0, 0);

  /** Per-instance visibility state — independent of section-level group visibility. */
  private _visibilityState: Map<GaugingElement, boolean> = new Map();

  /**
   * Flag set when new InstancedMesh objects are created (buildBatch/addInstance).
   * Consumed by the render loop to pre-compile shader programs before the first
   * render — avoids async shader compilation (KHR_parallel_shader_compile)
   * interfering with OutlinePass depth/mask renders.
   */
  private _needsCompile = false;

  /** Whether new meshes need shader pre-compilation. */
  get needsCompile(): boolean { return this._needsCompile; }

  /** Clear the compile flag after pre-compilation is done. */
  clearCompileFlag(): void { this._needsCompile = false; }

  /**
   * Pending element data for sections that have not yet been loaded.
   * DATA-03: elements arriving via SignalR before buildBatch are queued here
   * and drained when the section eventually loads.
   */
  private _pendingData: Map<string, ElementResultInfo[]> = new Map();

  // ---------------------------------------------------------------------------
  // Phase 4 fields — outline proxy pool
  // ---------------------------------------------------------------------------

  /** Idle proxy pool: shape type -> reusable Mesh[] (proxies stay in scene when idle). */
  private _proxyPool: Map<PointShapeType, THREE.Mesh[]> = new Map();

  /** Active proxies: element -> the proxy Mesh currently representing it in OutlinePass. */
  private _activeProxies: Map<GaugingElement, THREE.Mesh> = new Map();

  /** Reverse index: InstancedMesh -> PointShapeType for O(1) proxy pool lookup. */
  private _meshToShapeType: Map<THREE.InstancedMesh, PointShapeType> = new Map();

  /** Reusable Matrix4 for proxy positioning — avoids per-call allocation in _copyInstanceMatrixToProxy. */
  private _tempProxyMatrix: THREE.Matrix4 = new THREE.Matrix4();

  // ---------------------------------------------------------------------------
  // Color palette (REND-04)
  // Extracted directly from GraphicsMaterials static fields — exact hex values.
  // PointMaterialMagenta: 0xFF00FF confirmed from graphics-materials.ts line 384-388.
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Phase 3 drag color constants (SEL-02 / REND-06)
  // Allocated once as static readonly — drag service references these as
  // InspectionPointInstanceManager.DRAG_COLOR etc. without per-call allocation.
  // ---------------------------------------------------------------------------

  /** Yellow — element is being actively dragged. */
  static readonly DRAG_COLOR = new THREE.Color(0xffff00);

  /** Green — element is over a valid drop zone. */
  static readonly DRAG_VALID_COLOR = new THREE.Color(0x00ff00);

  /** Red — element is over an invalid drop zone. */
  static readonly DRAG_INVALID_COLOR = new THREE.Color(0xff0000);

  private static readonly _COLOR_MAP = new Map<string, THREE.Color>([
    ['PointMaterial',        new THREE.Color(0xe5e5e5)],
    ['PointMaterialGreen',   new THREE.Color(0x1c7024)],
    ['PointMaterialYellow',  new THREE.Color(0xf2ff00)],
    ['PointMaterialRed',     new THREE.Color(0xff0000)],
    ['PointMaterialBlack',   new THREE.Color(0x000000)],
    ['PointMaterialBlue',    new THREE.Color(0x0066ff)],
    ['PointMaterialMagenta', new THREE.Color(0xff00ff)],
    // A filter's Highlight (and a highlighted point being dragged or saved): the material's
    // blue with its orange glow, flattened, since instanced points are unlit. Without it the
    // point fell back to PointMaterial and turned white.
    ['SelectedBasicElementMaterial', new THREE.Color(0x4c14ff)],
  ]);

  constructor(private readonly geometryCache: PointShapeGeometryCache) {
    this._initMaterials();
  }

  // ---------------------------------------------------------------------------
  // Material pool (REND-05)
  // ---------------------------------------------------------------------------

  /**
   * Create one MeshBasicMaterial per PointShapeType with white base color,
   * DoubleSide, and polygonOffset settings matching the legacy PointMaterial
   * (factor: -20, units: -20).
   *
   * color: 0xffffff (white) ensures the final fragment color equals instanceColor
   * exactly (white * instanceColor = instanceColor). THREE.js activates the
   * USE_INSTANCING_COLOR shader define automatically when instanceColor exists on
   * the mesh — no vertexColors flag needed or wanted (geometries have no vertex
   * color attribute; vertexColors:true would zero out every instance color).
   */
  private _initMaterials(): void {
    for (const shapeType of Object.values(PointShapeType)) {
      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -20,
        polygonOffsetUnits: -20,
      });
      material.name = `InstancedPointMaterial_${shapeType}`;
      this._materials.set(shapeType, material);
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Create an InstancedMesh for the given (section, shapeType) pair.
   *
   * Phase 2 enhancements:
   *   - Force-allocates instanceColor buffer via setColorAt to avoid null NPE at flush (Pitfall 1).
   *   - Initializes ALL allocated slots to zero-scale matrix to prevent phantom shapes at origin (Pitfall 2).
   *   - Populates _savedMatrix for each element with its initial matrix — ensures safe restore
   *     if setInstanceVisible(false) is called before addInstance (Pitfall 5).
   *
   * Requirements satisfied:
   *   MGR-01 — registers the mesh so destroy() can clean it up
   *   MGR-02 — capacity equals elements.length
   *   REND-07 — StaticDrawUsage on instanceMatrix (bulk batches are written once at load, stable after that)
   *   MGR-05 — frustumCulled=true on bulk batches (bounding sphere reliable for section-level meshes)
   *
   * @throws Error if a batch for (section.Guid, shapeType) already exists.
   */
  buildBatch(
    section: Section,
    shapeType: PointShapeType,
    elements: GaugingElement[],
  ): THREE.InstancedMesh {
    // Duplicate guard (MGR-01)
    const sectionKey = section.Guid;
    if (this._meshes.get(sectionKey)?.has(shapeType)) {
      throw new Error(
        `InspectionPointInstanceManager.buildBatch: a batch for section "${sectionKey}" ` +
          `and shapeType "${shapeType}" already exists. ` +
          `Call destroy() or removeBatch() before rebuilding.`,
      );
    }

    const geo = this.geometryCache.get(shapeType);
    const mat = this._materials.get(shapeType)!;

    // Create InstancedMesh with exact capacity
    const im = new THREE.InstancedMesh(geo, mat, elements.length);

    // REND-07: static usage — bulk-loaded matrices are written once at load time.
    // StaticDrawUsage is a GPU driver hint to place the buffer in faster dedicated VRAM.
    // setMatrixAt/needsUpdate still works correctly regardless of usage mode.
    im.instanceMatrix.setUsage(THREE.StaticDrawUsage);

    // MGR-05: enable built-in frustum culling on bulk batches.
    // Each section-level InstancedMesh has a well-defined bounding sphere.
    // THREE.js will skip the entire draw call when the section is off-screen.
    // The zero-scale matrix trick for hidden instances is unaffected.
    im.frustumCulled = true;

    // Pitfall 1: force-allocate instanceColor by calling setColorAt on slot 0.
    // instanceColor is null until the first setColorAt — calling needsUpdate before
    // any setColorAt will NPE. Initialize all slots with the default empty-state color.
    const defaultColor = new THREE.Color(0xe5e5e5);
    for (let i = 0; i < elements.length; i++) {
      im.setColorAt(i, defaultColor);
    }
    im.instanceColor!.needsUpdate = true;

    // Pitfall 2: initialize ALL allocated slots to zero-scale matrix so un-written
    // slots render as invisible (not as phantom shapes at world origin).
    for (let i = 0; i < elements.length; i++) {
      im.setMatrixAt(i, this._ZERO_MATRIX);
    }
    im.instanceMatrix.needsUpdate = true;

    im.count = elements.length;

    // Register in reverse index
    this._instanceMap.set(im, new Array(elements.length).fill(null));

    // Register in primary storage
    if (!this._meshes.has(sectionKey)) {
      this._meshes.set(sectionKey, new Map());
    }
    this._meshes.get(sectionKey)!.set(shapeType, im);
    this._meshToShapeType.set(im, shapeType);

    this._needsCompile = true;

    return im;
  }

  /**
   * Wire a batch of GaugingElement instances into a mesh already created by buildBatch.
   *
   * buildBatch creates the mesh with exact capacity and all slots zeroed/grayed.
   * This method writes each element's matrix and color into the correct slot,
   * then populates _elementMap, _instanceMap, and _savedMatrix for each element.
   *
   * Must be called once per buildBatch call, in the same pass as buildBatch.
   * Do NOT mix registerBulkElements calls with addInstance calls for the same batch.
   *
   * @param mesh      The InstancedMesh returned by buildBatch.
   * @param instances Array of { element, matrix, color } in slot index order (0..n-1).
   */
  registerBulkElements(
    mesh: THREE.InstancedMesh,
    instances: { element: GaugingElement; matrix: THREE.Matrix4; color: THREE.Color }[],
  ): void {
    for (let i = 0; i < instances.length; i++) {
      const { element, matrix, color } = instances[i];
      mesh.setMatrixAt(i, matrix);
      mesh.setColorAt(i, color);
      this._savedMatrix.set(element, matrix.clone());
      this._elementMap.set(element, { mesh, instanceIndex: i });
      this._instanceMap.get(mesh)![i] = element;

      // VIS-07: Reconcile phantom visibility state after registration.
      //
      // checkElementVisibility and the GaugingElement.active setter may have
      // called SurfaceDesign.visible = false BEFORE this element was wired
      // into _elementMap. At that time, setInstanceVisible silently no-oped
      // because the element lookup failed (not yet registered).
      //
      // The PhantomSurfaceDesign._visible field was set to false, but the
      // InstancedMesh slot still contains the real (visible) matrix. This
      // desync causes the PhantomSurfaceDesign debounce (if _visible === v return)
      // to skip all subsequent hide calls, making the point permanently visible.
      //
      // Fix: read the phantom's visible getter and, if it says hidden, apply
      // the zero-scale matrix to the slot. Always initialize _visibilityState
      // so the idempotency guard in setInstanceVisible works correctly.
      const phantomVisible = element.SurfaceDesign?.visible ?? true;
      if (!phantomVisible) {
        // Element was hidden before registration — apply zero-scale now.
        mesh.setMatrixAt(i, this._ZERO_MATRIX);
        this._visibilityState.set(element, false);
      } else {
        this._visibilityState.set(element, true);
      }
    }
    this._matrixDirty.add(mesh);
    this._colorDirty.add(mesh);
  }

  /**
   * Add a single GaugingElement instance to the existing or newly created batch.
   *
   * Full Phase 2 implementation: writes matrix+color, handles buffer growth with 2x
   * capacity when the mesh is full, populates all internal maps.
   *
   * Buffer growth: when mesh.count >= capacity, creates a new InstancedMesh with 2x
   * capacity, copies all existing instance data, swaps in the scene graph, and disposes
   * the old mesh geometry-safely (shared geometry is NOT disposed).
   *
   * @param section  The section this element belongs to.
   * @param shapeType The PointShapeType of this element.
   * @param element   The GaugingElement being registered.
   * @param matrix    The world transform matrix for this instance.
   * @param color     The initial per-instance color (from _COLOR_MAP lookup at call site).
   */
  addInstance(
    section: Section,
    shapeType: PointShapeType,
    element: GaugingElement,
    matrix: THREE.Matrix4,
    color: THREE.Color,
  ): void {
    const sectionKey = section.Guid;
    let sectionMeshes = this._meshes.get(sectionKey);

    if (!sectionMeshes || !sectionMeshes.has(shapeType)) {
      // Create a seed batch with capacity 16 — more practical for SignalR growth
      const geo = this.geometryCache.get(shapeType);
      const mat = this._materials.get(shapeType)!;
      const im = new THREE.InstancedMesh(geo, mat, 16);
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      im.frustumCulled = false;
      im.count = 0;

      // Force-allocate instanceColor buffer (Pitfall 1)
      const defaultColor = new THREE.Color(0xe5e5e5);
      im.setColorAt(0, defaultColor);
      im.instanceColor!.needsUpdate = true;

      // Initialize all slots to zero-scale (Pitfall 2)
      for (let i = 0; i < 16; i++) {
        im.setMatrixAt(i, this._ZERO_MATRIX);
      }
      im.instanceMatrix.needsUpdate = true;

      this._instanceMap.set(im, new Array(16).fill(null));

      if (!this._meshes.has(sectionKey)) {
        this._meshes.set(sectionKey, new Map());
      }
      this._meshes.get(sectionKey)!.set(shapeType, im);
      this._meshToShapeType.set(im, shapeType);
      sectionMeshes = this._meshes.get(sectionKey)!;
      this._needsCompile = true;
    }

    let mesh = sectionMeshes.get(shapeType)!;
    const slots = this._instanceMap.get(mesh)!;
    const capacity = slots.length;

    // Buffer growth: when full, reallocate at 2x capacity
    if (mesh.count >= capacity) {
      const newCapacity = capacity * 2;
      const geo = this.geometryCache.get(shapeType);
      const mat = this._materials.get(shapeType)!;
      const newMesh = new THREE.InstancedMesh(geo, mat, newCapacity);
      newMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      newMesh.frustumCulled = false;

      // Force-allocate instanceColor on new mesh and initialize to zero-scale
      const defaultColor = new THREE.Color(0xe5e5e5);
      for (let i = 0; i < newCapacity; i++) {
        newMesh.setColorAt(i, defaultColor);
        newMesh.setMatrixAt(i, this._ZERO_MATRIX);
      }
      newMesh.instanceColor!.needsUpdate = true;
      newMesh.instanceMatrix.needsUpdate = true;

      // Copy all existing instance matrices and colors from old mesh
      const tempMatrix = new THREE.Matrix4();
      const tempColor = new THREE.Color();
      for (let i = 0; i < mesh.count; i++) {
        mesh.getMatrixAt(i, tempMatrix);
        newMesh.setMatrixAt(i, tempMatrix);
        if (mesh.instanceColor) {
          mesh.getColorAt(i, tempColor);
          newMesh.setColorAt(i, tempColor);
        }
      }
      newMesh.count = mesh.count;

      // Swap in scene graph (if old mesh has a parent)
      if (mesh.parent) {
        mesh.parent.add(newMesh);
        mesh.parent.remove(mesh);
      }

      // Build new slots array and migrate element references
      const newSlots = new Array<GaugingElement | null>(newCapacity).fill(null);
      for (let i = 0; i < slots.length; i++) {
        newSlots[i] = slots[i];
      }

      // Migrate all _elementMap entries from old mesh to new mesh
      for (const [el, lookup] of this._elementMap.entries()) {
        if (lookup.mesh === mesh) {
          this._elementMap.set(el, { mesh: newMesh, instanceIndex: lookup.instanceIndex });
        }
      }

      // Update dirty sets to reference new mesh
      if (this._matrixDirty.has(mesh)) {
        this._matrixDirty.delete(mesh);
        this._matrixDirty.add(newMesh);
      }
      if (this._colorDirty.has(mesh)) {
        this._colorDirty.delete(mesh);
        this._colorDirty.add(newMesh);
      }

      // Dispose old mesh (NOT geometry — it is shared)
      mesh.dispose();

      // Register new mesh
      this._instanceMap.delete(mesh);
      this._instanceMap.set(newMesh, newSlots);
      sectionMeshes.set(shapeType, newMesh);
      this._meshToShapeType.delete(mesh);
      this._meshToShapeType.set(newMesh, shapeType);
      mesh = newMesh;
    }

    const instanceIndex = mesh.count;

    // Write matrix
    mesh.setMatrixAt(instanceIndex, matrix);

    // Write color
    mesh.setColorAt(instanceIndex, color);

    // Pitfall 5: always save initial matrix so restore works even if hidden before first addInstance
    this._savedMatrix.set(element, matrix.clone());

    // Update forward and reverse indexes
    this._elementMap.set(element, { mesh, instanceIndex });
    this._instanceMap.get(mesh)![instanceIndex] = element;

    // Increment rendered count
    mesh.count++;

    // Mark dirty for flush
    this._matrixDirty.add(mesh);
    this._colorDirty.add(mesh);
  }

  /**
   * Show or hide a single instance via the zero-scale matrix trick (VIS-02).
   *
   * Hide: saves current matrix in _savedMatrix, applies zero-scale.
   * Show: restores saved matrix from _savedMatrix.
   *
   * Updates _visibilityState for per-instance state tracking.
   */
  setInstanceVisible(element: GaugingElement, visible: boolean): void {
    const lookup = this._elementMap.get(element);
    if (!lookup) return;

    // Idempotency guard: skip if already in the requested state.
    // Prevents _savedMatrix corruption when called twice with the same value
    // (second hide would save the zero matrix, permanently losing the real matrix).
    // Note: first call for a new element has currentState === undefined, which is
    // !== visible for both true and false — the guard does not block initial calls.
    const currentState = this._visibilityState.get(element);
    if (currentState === visible) return;

    const { mesh, instanceIndex } = lookup;

    if (visible) {
      // Restore saved matrix
      const saved = this._savedMatrix.get(element);
      if (saved) {
        mesh.setMatrixAt(instanceIndex, saved);
      }
    } else {
      // Save current matrix before zeroing
      const current = new THREE.Matrix4();
      mesh.getMatrixAt(instanceIndex, current);
      this._savedMatrix.set(element, current);
      mesh.setMatrixAt(instanceIndex, this._ZERO_MATRIX);
    }

    this._matrixDirty.add(mesh);
    this._visibilityState.set(element, visible);
  }

  /**
   * Return the per-instance visibility state.
   * Defaults to true (visible) for instances not yet tracked.
   */
  getInstanceVisible(element: GaugingElement): boolean {
    return this._visibilityState.get(element) ?? true;
  }

  // ---------------------------------------------------------------------------
  // Read-only accessors (no side effects on buffers, dirty sets or visibility)
  // ---------------------------------------------------------------------------

  /** Batch data of a registered element, or null. */
  getInstanceInfo(
    element: GaugingElement,
  ): { mesh: THREE.InstancedMesh; instanceIndex: number; shapeType: PointShapeType } | null {
    const lookup = this._elementMap.get(element);
    if (!lookup) return null;
    const shapeType = this._meshToShapeType.get(lookup.mesh);
    if (shapeType === undefined) return null;
    return { mesh: lookup.mesh, instanceIndex: lookup.instanceIndex, shapeType };
  }

  /**
   * World matrix of the element's slot (instance matrix premultiplied by mesh.matrixWorld);
   * false when not registered.
   *
   * The batch's world matrix is refreshed (ancestors included) before composing, so a
   * section group moved since the last render is honoured. For a hidden element the slot
   * holds the zero-scale matrix; the saved pose that setInstanceVisible(true) would restore
   * is used instead. The slot and the visibility state are not modified.
   */
  getInstanceWorldMatrix(element: GaugingElement, target: THREE.Matrix4): boolean {
    const lookup = this._elementMap.get(element);
    if (!lookup) return false;

    const saved = this._visibilityState.get(element) === false ? this._savedMatrix.get(element) : undefined;
    if (saved) {
      target.copy(saved);
    } else {
      lookup.mesh.getMatrixAt(lookup.instanceIndex, target);
    }
    lookup.mesh.updateWorldMatrix(true, false);
    target.premultiply(lookup.mesh.matrixWorld);
    return true;
  }

  /** Current instance colour copied into target; false when not registered. */
  getInstanceColor(element: GaugingElement, target: THREE.Color): boolean {
    const lookup = this._elementMap.get(element);
    if (!lookup || !lookup.mesh.instanceColor) return false;
    lookup.mesh.getColorAt(lookup.instanceIndex, target);
    return true;
  }

  /**
   * Override the color of a single instance by mapping material.name to a
   * THREE.Color from _COLOR_MAP. Falls back to PointMaterial (grey) for unknown names.
   *
   * Adds mesh to _colorDirty — GPU upload deferred to flushDirtyBuffers() (MGR-03).
   *
   * @param element  The GaugingElement whose color to update.
   * @param material The material whose .name drives the color lookup (e.g. 'PointMaterialRed').
   */
  setInstanceColor(element: GaugingElement, material: THREE.Material): void {
    const lookup = this._elementMap.get(element);
    if (!lookup) return;

    const color =
      InspectionPointInstanceManager._COLOR_MAP.get(material.name) ??
      InspectionPointInstanceManager._COLOR_MAP.get('PointMaterial')!;

    lookup.mesh.setColorAt(lookup.instanceIndex, color);
    this._colorDirty.add(lookup.mesh);
  }

  /**
   * Write a THREE.Color directly to the instanceColor buffer for `element`,
   * bypassing the material.name -> _COLOR_MAP lookup used by setInstanceColor.
   *
   * Used by the drag service to apply interactive feedback colors (yellow, green,
   * red) that are direct hex values rather than named materials. Adds mesh to
   * _colorDirty for deferred GPU upload at next flushDirtyBuffers() call.
   *
   * @param element The GaugingElement whose instance color to update.
   * @param color   The THREE.Color to write directly to the instanceColor buffer.
   */
  setInstanceColorDirect(element: GaugingElement, color: THREE.Color): void {
    const lookup = this._elementMap.get(element);
    if (!lookup) return;

    lookup.mesh.setColorAt(lookup.instanceIndex, color);
    this._colorDirty.add(lookup.mesh);
  }

  /**
   * Return the default color for a newly inserted inspection point.
   *
   * This is the "empty" / no-measurement grey color from PointMaterial.
   * Returns a clone of the shared static instance — callers must not mutate
   * the shared color instance directly.
   *
   * @returns A cloned THREE.Color for the default grey insertion color.
   */
  static getDefaultColor(): THREE.Color {
    return InspectionPointInstanceManager._COLOR_MAP.get('PointMaterial')!.clone();
  }

  /**
   * Flush dirty matrix and color buffers to the GPU.
   *
   * Must be called once per animation frame, before renderer.render().
   * Only sets needsUpdate on meshes that actually had writes since the last flush
   * (MGR-03 — avoids full-buffer uploads on every frame).
   */
  flushDirtyBuffers(): void {
    for (const mesh of this._matrixDirty) {
      mesh.instanceMatrix.needsUpdate = true;
      // Instance matrices changed — invalidate the cached bounding sphere.
      // InstancedMesh.raycast computes the sphere ONCE (lazily) and THREE never
      // refreshes it on setMatrixAt: a stale sphere makes moved/added points
      // fail the raycast early-out and become unclickable. Nulling it triggers
      // a lazy recompute on the next raycast (and frustum-culling check).
      mesh.boundingSphere = null;
    }
    this._matrixDirty.clear();

    for (const mesh of this._colorDirty) {
      if (mesh.instanceColor) {
        mesh.instanceColor.needsUpdate = true;
      }
    }
    this._colorDirty.clear();
  }

  /** Check if any buffers are pending GPU upload. */
  hasDirtyBuffers(): boolean {
    return this._matrixDirty.size > 0 || this._colorDirty.size > 0;
  }

  /**
   * Return the per-shape InstancedMesh map for a given section.
   * Returns undefined if no batch exists for the section yet.
   *
   * Used by Plan 02-02 to add InstancedMesh objects to section.surfaceGroup
   * after batch creation.
   */
  getMeshesForSection(section: Section): Map<PointShapeType, THREE.InstancedMesh> | undefined {
    return this._meshes.get(section.Guid);
  }

  /**
   * Resolve a raycast hit back to its GaugingElement.
   * Returns null until addInstance populates the instance map for the hit slot.
   */
  resolveInstance(mesh: THREE.InstancedMesh, instanceId: number): GaugingElement | null {
    return this._instanceMap.get(mesh)?.[instanceId] ?? null;
  }

  /**
   * Return all InstancedMesh objects managed by this service.
   * Used by the raycaster to perform hit tests against all batches.
   */
  getRaycastTargets(): THREE.InstancedMesh[] {
    const targets: THREE.InstancedMesh[] = [];
    for (const shapeMap of this._meshes.values()) {
      for (const mesh of shapeMap.values()) {
        targets.push(mesh);
      }
    }
    return targets;
  }

  // ---------------------------------------------------------------------------
  // Phase 3 fields
  // ---------------------------------------------------------------------------

  /** Reusable Object3D for composing transform matrices — avoids per-call allocation. */
  private readonly _dummy = new THREE.Object3D();

  // ---------------------------------------------------------------------------
  // Phase 3: Drag position sync (REND-06)
  // ---------------------------------------------------------------------------

  /**
   * Synchronise the InstancedMesh slot for `element` with the current transform
   * of its PhantomSurfaceDesign (position / quaternion / scale).
   *
   * Two upload paths:
   *   - drag=true  → addUpdateRange(offset, 16) for partial GPU upload (REND-06).
   *                  Does NOT add mesh to _matrixDirty — mixing addUpdateRange with
   *                  full-buffer needsUpdate on the same buffer causes corruption
   *                  (Pitfall 3 from research).
   *   - drag=false → adds mesh to _matrixDirty (normal deferred flush path).
   *
   * Also updates _savedMatrix so that hide/show after a drag restores to the
   * correct (post-drag) position and not the stale pre-drag position.
   *
   * @param element The GaugingElement whose instance to sync.
   * @param drag    When true, uses partial buffer upload via addUpdateRange.
   */
  syncInstance(element: GaugingElement, drag?: boolean): void {
    const lookup = this._elementMap.get(element);
    if (!lookup) return;

    const phantom = element.SurfaceDesign;
    if (!phantom) return;

    // Compose matrix from phantom transform
    this._dummy.position.copy(phantom.position);
    this._dummy.quaternion.copy(phantom.quaternion);
    this._dummy.scale.copy(phantom.scale);
    this._dummy.updateMatrix();

    // Write composed matrix to the InstancedMesh slot
    lookup.mesh.setMatrixAt(lookup.instanceIndex, this._dummy.matrix);

    // Keep saved matrix in sync so hide/show after drag restores to correct position
    this._savedMatrix.set(element, this._dummy.matrix.clone());

    if (drag) {
      // REND-06: partial buffer upload — only upload the 16 floats for this slot.
      // Do NOT add to _matrixDirty — addUpdateRange and _matrixDirty must not mix
      // on the same buffer (Pitfall 3).
      const attr = lookup.mesh.instanceMatrix;
      attr.addUpdateRange(lookup.instanceIndex * 16, 16);
      attr.needsUpdate = true;
      // The drag path bypasses flushDirtyBuffers — invalidate the cached bounding
      // sphere here so raycasts during/after the drag see the moved instance.
      lookup.mesh.boundingSphere = null;
    } else {
      // Normal deferred flush path
      this._matrixDirty.add(lookup.mesh);
    }
  }

  /**
   * Remove an instance from its InstancedMesh using swap-and-pop compaction
   * (Pattern 3 from research). Used for cancelled insertions.
   *
   * Algorithm:
   *   1. Resolve element lookup.
   *   2. Read last-slot matrix and color.
   *   3. Write last-slot data into vacated slot.
   *   4. Re-index maps for the element that moved (savedMatrix is untouched).
   *   5. Zero the last slot and decrement mesh.count.
   *   6. Clean up all maps for the removed element.
   *   7. Mark mesh dirty for next flush.
   *
   * @param element The GaugingElement to remove.
   */
  removeInstance(element: GaugingElement): void {
    const lookup = this._elementMap.get(element);
    if (!lookup) return;

    // An outlined (selected / pivot) element must not leave its proxy behind.
    this.releaseProxy(element);

    const { mesh, instanceIndex } = lookup;
    const lastIndex = mesh.count - 1;

    const tempMatrix = new THREE.Matrix4();
    const tempColor = new THREE.Color();

    if (instanceIndex !== lastIndex) {
      // Read last slot data
      mesh.getMatrixAt(lastIndex, tempMatrix);
      if (mesh.instanceColor) {
        mesh.getColorAt(lastIndex, tempColor);
      }

      // Write last slot data into the vacated slot
      mesh.setMatrixAt(instanceIndex, tempMatrix);
      if (mesh.instanceColor) {
        mesh.setColorAt(instanceIndex, tempColor);
      }

      // Find the element that was at the last slot
      const slots = this._instanceMap.get(mesh)!;
      const lastElement = slots[lastIndex] as GaugingElement;

      // Update all maps for the element that moved from lastIndex -> instanceIndex
      slots[instanceIndex] = lastElement;
      this._elementMap.set(lastElement, { mesh, instanceIndex });
      // _savedMatrix is indexed by element, not by slot, and already holds the
      // moved element's real matrix (registerBulkElements/addInstance/syncInstance
      // keep it current). Overwriting it with the slot content would store the
      // zero-scale tombstone when the moved element is hidden, so it could never
      // be shown again.
    }

    // Zero the last slot (now vacated by move or was already the target slot)
    mesh.setMatrixAt(lastIndex, this._ZERO_MATRIX);

    // Decrement count
    mesh.count--;

    // Clean up maps for the removed element
    const slots = this._instanceMap.get(mesh)!;
    slots[lastIndex] = null;
    this._elementMap.delete(element);
    this._savedMatrix.delete(element);
    this._visibilityState.delete(element);

    // Mark dirty for next flush
    this._matrixDirty.add(mesh);
    this._colorDirty.add(mesh);
  }

  // ---------------------------------------------------------------------------
  // Pending data queue (DATA-03)
  // ---------------------------------------------------------------------------

  /**
   * Queue element data for a section that has not yet been loaded.
   *
   * Elements arriving via SignalR before buildBatch is called for their section
   * are buffered here. When the section eventually loads, drainPendingData()
   * retrieves the buffered elements so they can be included in buildBatch.
   *
   * Implements the locked decision: "Points arriving for unloaded sections are
   * queued — buffered data included in buildBatch when section eventually loads."
   *
   * @param sectionGuid The GUID of the not-yet-loaded section.
   * @param elements    The element results to queue.
   */
  queueForSection(sectionGuid: string, elements: ElementResultInfo[]): void {
    const existing = this._pendingData.get(sectionGuid);
    if (existing) {
      existing.push(...elements);
    } else {
      this._pendingData.set(sectionGuid, [...elements]);
    }
  }

  /**
   * Remove and return the pending element data for a section.
   *
   * Returns undefined if no data was queued for the given section.
   * Uses Map.delete to ensure data is consumed exactly once.
   *
   * @param sectionGuid The GUID of the section being loaded.
   * @returns The queued ElementResultInfo array, or undefined if none exists.
   */
  drainPendingData(sectionGuid: string): ElementResultInfo[] | undefined {
    const data = this._pendingData.get(sectionGuid);
    if (data !== undefined) {
      this._pendingData.delete(sectionGuid);
    }
    return data;
  }

  // ---------------------------------------------------------------------------
  // Phase 4: Outline proxy pool (SEL-04)
  // ---------------------------------------------------------------------------

  /**
   * Acquire a proxy Mesh for the given element to outline via the selection outline pass.
   * The proxy uses shared geometry from PointShapeGeometryCache (no clone),
   * has matrixAutoUpdate=false, and its matrixWorld is set to the instance's world matrix.
   * If the element already has an active proxy, returns it (idempotent).
   * Returns null if the element is not registered in the manager.
   */
  acquireProxy(element: GaugingElement, scene: THREE.Scene): THREE.Mesh | null {
    // Idempotency: if already active, just update matrix and return
    const existing = this._activeProxies.get(element);
    if (existing) {
      this._copyInstanceMatrixToProxy(element, existing);
      return existing;
    }

    const lookup = this._elementMap.get(element);
    if (!lookup) return null;

    const shapeType = this._meshToShapeType.get(lookup.mesh);
    if (shapeType == null) return null;

    // Try to reuse a pooled proxy for this shape type
    const pool = this._proxyPool.get(shapeType);
    let proxy = pool?.pop() ?? null;

    if (!proxy) {
      // Create new proxy — shared geometry reference (NOT clone), invisible material
      proxy = new THREE.Mesh(
        this.geometryCache.get(shapeType),
        new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false }),
      );
      proxy.matrixAutoUpdate = false;   // CRITICAL: we control matrixWorld directly
      proxy.frustumCulled = false;      // always render while outlined
      this._needsCompile = true;        // trigger shader pre-compilation before first render
      scene.add(proxy);                 // MUST be in scene for OutlinePass mask
    }

    // Ensure proxy is visible (OutlinePass reads visibility state)
    proxy.visible = true;

    // Copy the instance world matrix into the proxy
    this._copyInstanceMatrixToProxy(element, proxy);

    this._activeProxies.set(element, proxy);
    return proxy;
  }

  /**
   * Release the proxy for the given element back to the pool.
   * The caller is responsible for removing the proxy from the outline pass selection.
   * The proxy stays in the scene (no scene.remove) to avoid scene-graph churn.
   */
  releaseProxy(element: GaugingElement): void {
    const proxy = this._activeProxies.get(element);
    if (!proxy) return;

    this._activeProxies.delete(element);
    proxy.visible = false;  // hide from main render pass while pooled

    // Defense-in-depth: strip outline layer bits so a pooled proxy can never leak
    // into the selection mask. (.visible = false already excludes it from the mask
    // render; this guards against any path that re-shows the proxy while pooled.)
    proxy.layers.disable(OUTLINE_LAYER_PRIMARY);
    proxy.layers.disable(OUTLINE_LAYER_SECONDARY);

    // Return to the correct shape-type pool slot
    const lookup = this._elementMap.get(element);
    if (lookup) {
      const shapeType = this._meshToShapeType.get(lookup.mesh);
      if (shapeType != null) {
        if (!this._proxyPool.has(shapeType)) {
          this._proxyPool.set(shapeType, []);
        }
        this._proxyPool.get(shapeType)!.push(proxy);
      }
    }
  }

  /**
   * Update the proxy matrixWorld to match the current instance matrix.
   * Called during drag to keep the outline following the moving point.
   */
  syncProxyMatrix(element: GaugingElement): void {
    const proxy = this._activeProxies.get(element);
    if (!proxy) return;
    this._copyInstanceMatrixToProxy(element, proxy);
  }

  /**
   * Release ALL active proxies back to the pool. Called on deselect-all / clear.
   */
  releaseAllProxies(): void {
    for (const [element] of this._activeProxies) {
      this.releaseProxy(element);
    }
  }

  /** Compose the instance slot's world matrix (local * InstancedMesh.matrixWorld) into proxy.matrix. */
  private _copyInstanceMatrixToProxy(element: GaugingElement, proxy: THREE.Mesh): void {
    const lookup = this._elementMap.get(element);
    if (!lookup) return;

    // Step 1: get the LOCAL instance matrix from the InstancedMesh buffer
    lookup.mesh.getMatrixAt(lookup.instanceIndex, this._tempProxyMatrix);

    // Step 2: compose with InstancedMesh world matrix to get the instance's WORLD matrix.
    // getMatrixAt returns the transform relative to the InstancedMesh's local space.
    // The InstancedMesh is parented to section.group which may have a non-identity world
    // transform. premultiply computes: result = lookup.mesh.matrixWorld * instanceLocalMatrix.
    this._tempProxyMatrix.premultiply(lookup.mesh.matrixWorld);

    // Step 3: write to proxy.matrix (NOT just matrixWorld).
    // The proxy is a direct child of the scene with matrixAutoUpdate=false.
    // Writing only to matrixWorld is fragile: when THREE.js renderer calls
    // scene.updateMatrixWorld() with force=true (happens during scene setup, asset
    // reload, etc.), it recomputes proxy.matrixWorld = parent.matrixWorld * proxy.matrix.
    // If proxy.matrix is still identity, the proxy snaps to origin.
    // Writing to proxy.matrix and flagging matrixWorldNeedsUpdate lets THREE.js
    // compute proxy.matrixWorld = scene.matrixWorld * proxy.matrix = I * worldMatrix
    // = worldMatrix correctly on every frame. Since matrixAutoUpdate=false, THREE.js
    // will NOT overwrite proxy.matrix from position/quaternion/scale decomposition.
    proxy.matrix.copy(this._tempProxyMatrix);
    proxy.matrixWorldNeedsUpdate = true;
  }

  // ---------------------------------------------------------------------------
  // Disposal
  // ---------------------------------------------------------------------------

  /**
   * Full teardown: dispose all proxy Meshes (active and pooled), then dispose all
   * InstancedMesh objects and private materials, then clear all internal maps.
   *
   * Called from graphics.destroy() BEFORE ThreeJsCleanup scene traversal so that
   * proxy Meshes (which reference shared BufferGeometry from PointShapeGeometryCache)
   * are removed from the scene before ThreeJsCleanup can accidentally dispose them.
   *
   * Note: Shared geometries from PointShapeGeometryCache are NOT disposed here
   * (userData.shared=true signals they are owned by the cache, not the meshes).
   * (MGR-01 / SEL-04)
   */
  destroy(): void {
    console.log('💣 Destroy InspectionPointInstanceManager');

    // 1. Remove active proxies from scene and dispose their materials
    for (const proxy of this._activeProxies.values()) {
      if (proxy.parent) proxy.parent.remove(proxy);
      (proxy.material as THREE.Material).dispose();
    }
    this._activeProxies.clear();

    // 2. Remove pooled proxies from scene and dispose their materials
    for (const pool of this._proxyPool.values()) {
      for (const proxy of pool) {
        if (proxy.parent) proxy.parent.remove(proxy);
        (proxy.material as THREE.Material).dispose();
      }
    }
    this._proxyPool.clear();

    // 3. Dispose all InstancedMesh objects (geometry is shared — skip it)
    for (const shapeMap of this._meshes.values()) {
      for (const mesh of shapeMap.values()) {
        // Do NOT call mesh.geometry.dispose() — geometry is shared
        mesh.dispose();
      }
    }
    this._meshes.clear();
    this._instanceMap.clear();
    this._elementMap.clear();

    // 4. Dispose private materials (they are NOT shared)
    for (const material of this._materials.values()) {
      material.dispose();
    }
    this._materials.clear();

    // 5. Clear Phase 2 state
    this._matrixDirty.clear();
    this._colorDirty.clear();
    this._savedMatrix.clear();
    this._visibilityState.clear();
    this._pendingData.clear();

    // 6. Clear Phase 4 reverse index
    this._meshToShapeType.clear();
  }
}
